import React, { useEffect, useState, useRef } from 'react';
import './blog.css';
import '../App.css';
import IntroVideo from '../images/purple_sparkles.mp4';
import VideoComponent from './VideoComponent.js';
import { Helmet } from "react-helmet";
import { motion, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollBehavior } from './useScrollBehavior.js';
import Footer from './footer.js';

const Blog = () => {

  useScrollBehavior();

  const navigate = useNavigate();

  const headingRef = useRef(null);
  const cardsRef = useRef(null);

  const isHeadingInView = useInView(headingRef, { once: true, amount: 0.45 });

  const [isVisibleCards, setIsVisibleCards] = useState(false);

  const letterAnimation = {
    hidden: { opacity: 0 },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.35,
      },
    }),
  };

  const renderAnimatedLettersByWord = (text, isInView) => {
    const words = text.split(' ');
    let k = 0;

    return words.map((word, wi) => (
      <span className="wordWrap_blog" key={wi}>
        {word.split('').map((char, ci) => (
          <motion.span
            key={`${wi}-${ci}`}
            custom={k++}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={letterAnimation}
            className="char_blog"
          >
            {char}
          </motion.span>
        ))}
        {wi < words.length - 1 ? ' ' : null}
      </span>
    ));
  };

  const handleScrollBlog = () => {
    if (cardsRef.current) {
      const rect = cardsRef.current.getBoundingClientRect();
      const triggerOffset = 180;
      const triggerPoint = window.innerHeight - triggerOffset;
      const isAboveTriggerPoint = rect.top < triggerPoint;
      setIsVisibleCards(isAboveTriggerPoint);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScrollBlog);
    handleScrollBlog();

    return () => {
      window.removeEventListener('scroll', handleScrollBlog);
    };
  }, []);

  const posts = [
    {
      slug: 'ai-chatbots-for-small-business',
      category: 'AI Systems',
      title: 'AI Chatbots for Small Business: What Actually Helps',
      date: 'March 12, 2026',
      excerpt:
        'A practical breakdown of where chatbots improve lead flow, where they fail, and how to make them useful instead of decorative.',
    },
    {
      slug: 'websites-that-convert',
      category: 'Web Development',
      title: 'What Makes a Business Website Convert Instead of Just Existing',
      date: 'March 6, 2026',
      excerpt:
        'The structural decisions that separate a brochure site from a site that generates calls, leads, and actual business.',
    },
    {
      slug: 'local-seo-signals-2026',
      category: 'SEO',
      title: 'Local SEO Signals That Still Matter in 2026',
      date: 'February 25, 2026',
      excerpt:
        'A focused look at the local SEO signals that still matter for relevance, visibility, authority, and qualified traffic.',
    },
    {
      slug: 'clean-design-wins',
      category: 'Design',
      title: 'Why Clean Design Usually Outperforms Clever Design',
      date: 'February 14, 2026',
      excerpt:
        'When design becomes confusing, users leave. Here is what actually improves trust, clarity, and engagement.',
    },
    {
      slug: 'subscription-web-model',
      category: 'Strategy',
      title: 'When a Subscription Website Model Makes Sense',
      date: 'February 2, 2026',
      excerpt:
        'A straightforward way to decide whether ongoing monthly support is better than a one-time website project.',
    },
    {
      slug: 'seo-and-speed',
      category: 'Performance',
      title: 'Website Speed Fixes That Help UX and SEO at the Same Time',
      date: 'January 23, 2026',
      excerpt:
        'The speed improvements that matter most for business websites and why performance affects more than rankings.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Blog | Yokai Software</title>
        <meta
          name="description"
          content="Read articles from Yokai Software on web development, SEO, AI systems, design, and digital strategy."
        />
        <link rel="canonical" href="https://yokaisoftware.com/blog" />
      </Helmet>

      <div id='blog_page_wrapper'>
        <VideoComponent  videoSrc={IntroVideo} />
        <div className="module_blog_page" id="blog_module_one">
          <div id='blog_hero_inner'>
            <div id='blog_heading_container' ref={headingRef}>
              <div id='blog_heading1'>
                {renderAnimatedLettersByWord("Yokai", isHeadingInView)}
              </div>
              <div id='blog_heading2'>
                {renderAnimatedLettersByWord("Insights & Articles", isHeadingInView)}
              </div>
              <div id='blog_heading3'>
                {renderAnimatedLettersByWord("Web Development, SEO, AI, and Digital Strategy", isHeadingInView)}
              </div>
            </div>

            <div id='blog_hero_side'>
              <div id='blog_hero_side_card'>
                <div id='blog_hero_side_icon'>
                  
                </div>
                <h2>Practical content</h2>
                <p>
                  Useful articles built for businesses that need stronger websites,
                  better visibility, and more effective digital systems.
                </p>

                <div
                  className='blog_cta_box'
                  onClick={() => {
                    navigate('/contact2');
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className='blog_cta_plus'>+</div>
                  <div className='blog_cta_text'>
                    <h3>Start a Project</h3>
                    <p>Web, SEO, AI, and strategy support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="module_blog_page" id="blog_module_two">
          <div id='blog_featured_wrap'>
            <div id='blog_featured_left'>
              <div id='blog_featured_label'>Featured Post</div>
              <h1>How Small Businesses Should Think About AI on Their Website</h1>
              <p>
                AI can improve intake, lead qualification, support workflows, and content systems,
                but only if the structure behind it makes sense. This is where most businesses get it wrong.
              </p>

              <div id='blog_featured_meta'>
                <span>March 12, 2026</span>
                <span>AI Systems</span>
              </div>

              <button
                id='blog_featured_btn'
                type='button'
                onClick={() => {
                  navigate('/blog/ai-chatbots-for-small-business');
                  window.scrollTo(0, 0);
                }}
              >
                Read Featured Article
              </button>
            </div>

            <div id='blog_featured_right'>
              <div id='blog_featured_visual'>
                <div id='blog_featured_pic'></div>
              </div>
            </div>
          </div>
        </div>

        <div className="module_blog_page" id="blog_module_three">
          <div id='blog_posts_heading_wrap'>
            <div id='blog_posts_heading'>
              <h1>Latest Posts</h1>
              <h2>Useful breakdowns. No filler.</h2>
            </div>
            <div id='blog_posts_arrow'>
             
            </div>
          </div>

          <div id='blog_posts_grid' ref={cardsRef}>
            {posts.map((post, index) => (
              <article
                key={post.slug}
                className={`blog_post_box ${isVisibleCards ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 0.18}s` }}
                onClick={() => {
                  navigate(`/blog/${post.slug}`);
                  window.scrollTo(0, 0);
                }}
              >
                <div className='blog_post_box_inner'>
                  <div className='blog_post_category'>{post.category}</div>
                  <h3>{post.title}</h3>
                  <div className='blog_post_date'>{post.date}</div>
                  <p>{post.excerpt}</p>

                  <div className='blog_post_bottom'>
                    <button className='blog_post_btn' type='button'>
                      Read More
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="module_blog_page" id="blog_module_four">
          <div id='blog_cta_strip'>
            <div id='blog_cta_strip_text'>
              <h1>Need a stronger website, better SEO, or AI-driven features?</h1>
              <p>
                Yokai Software builds digital systems designed for visibility, growth, and performance.
              </p>
            </div>

            <div id='blog_cta_strip_links'>
              <Link to="/contact2" className="blog_strip_btn">Contact</Link>
              <Link to="/services1" className="blog_strip_btn blog_strip_btn_alt">Services</Link>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Blog;